'use client';

import { useEffect, useRef } from 'react';

import { GENTOO_SDK_SRC, SCRIPT_KEYS } from '@/shared/constants/scripts';
import { useFloatingLayer } from '@/shared/contexts/FloatingLayerContext';
import { useScriptLoader } from '@/shared/contexts/ScriptLoaderContext';
import {
  GENTOO_PARTNER_ID,
  callGentoo,
  getGentooAuthCode,
} from '@/shared/lib/gentoo';

/**
 * Gentoo SDK 전역 초기화.
 *
 * - SDK 스크립트를 최초 1회 로드.
 * - `FloatingLayerContext` 의 zento 슬롯을 구독해 페이지 컨텍스트에 맞춰
 *   `boot` (partnerId/authCode/displayLocation/itemId) 재호출.
 * - `init` 은 항상 `isCustomButton: true, showGentooButton: false` —
 *   플로팅 UI 는 프로젝트 자체 <ZentoButton /> 또는 페이지 커스텀 렌더러가 담당,
 *   SDK 는 `#gentoo-custom-button` 클릭만 잡아 채팅창을 연다.
 * - zento 슬롯이 명시적으로 hidden (`{ visible: false }`) 이면 `unmount`.
 *
 * partnerId 는 `NEXT_PUBLIC_GENTOO_PARTNER_ID` 로 주입. 값이 비어있으면
 * SDK 로드/부팅을 건너뛴다 (환경 미설정 상태에서 무해하게 동작).
 */
export function GentooInitializer() {
  const { zento } = useFloatingLayer();
  const { loadScript, isLoaded } = useScriptLoader();

  const visible = zento?.visible ?? true;
  const displayLocation = zento?.displayLocation ?? 'HOME';
  const itemId = zento?.itemId;
  const buttonMode = zento?.buttonMode ?? 'custom';

  useEffect(() => {
    if (!GENTOO_PARTNER_ID) return;
    loadScript(SCRIPT_KEYS.GENTOO, GENTOO_SDK_SRC).catch(() => {
      // 네트워크 실패 — 채팅은 안 열리지만 나머지 UX 는 영향 없음
    });
  }, [loadScript]);

  const loaded = isLoaded(SCRIPT_KEYS.GENTOO);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!GENTOO_PARTNER_ID || !loaded) return;

    if (!visible) {
      if (mountedRef.current) {
        callGentoo('unmount', {});
        mountedRef.current = false;
      }
      return;
    }

    callGentoo('boot', {
      partnerId: GENTOO_PARTNER_ID,
      authCode: getGentooAuthCode(),
      displayLocation,
      ...(itemId ? { itemId } : {}),
    });
    callGentoo('init', {
      isCustomButton: buttonMode === 'custom',
      showGentooButton: buttonMode === 'sdk',
    });
    mountedRef.current = true;
  }, [loaded, visible, displayLocation, itemId, buttonMode]);

  return null;
}
